import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { isOnboardingTourActive } from '@/lib/onboardingTour';
import {
  FEATURE_HINTS,
  disableAllHints,
  dismissHint,
  isHintHidden,
  snoozeHint,
  type FeatureHint,
} from '@/lib/featureHints';
import { mixpanelService } from '@/lib/mixpanelService';

const POPOVER_WIDTH = 300;
const GAP = 12;
const PAD = 12;
const SCAN_INTERVAL_MS = 700;

interface Anchor {
  hint: FeatureHint;
  rect: DOMRect;
}

const visibleRect = (selector: string): DOMRect | null => {
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  const inView =
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth;
  return inView ? rect : null;
};

const position = (rect: DOMRect, placement: FeatureHint['placement']): React.CSSProperties => {
  const width = Math.min(POPOVER_WIDTH, window.innerWidth - PAD * 2);
  const clampLeft = (left: number) =>
    Math.min(Math.max(left, PAD), window.innerWidth - width - PAD);
  const clampTop = (top: number) => Math.min(Math.max(top, PAD), window.innerHeight - 200);
  // Fall back to "bottom" when there is no horizontal room (phones).
  const effective =
    (placement === 'right' && rect.right + GAP + width > window.innerWidth - PAD) ||
    (placement === 'left' && rect.left - GAP - width < PAD)
      ? 'bottom'
      : placement;
  switch (effective) {
    case 'right':
      return { width, left: rect.right + GAP, top: clampTop(rect.top - 4) };
    case 'left':
      return { width, left: rect.left - GAP - width, top: clampTop(rect.top - 4) };
    case 'top':
      return { width, left: clampLeft(rect.left), bottom: window.innerHeight - rect.top + GAP };
    default:
      return { width, left: clampLeft(rect.left), top: clampTop(rect.bottom + GAP) };
  }
};

/** Shows one "what's new" tip at a time next to new features, until dismissed. */
export const FeatureHints: React.FC = () => {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const userKey = user ? String(user.id ?? user.username) : null;
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [, setVersion] = useState(0);

  const scan = useCallback(() => {
    if (!userKey || isOnboardingTourActive()) {
      setAnchor(null);
      return;
    }
    for (const hint of FEATURE_HINTS) {
      if (!hint.route(location.pathname) || isHintHidden(userKey, hint.id)) continue;
      const rect = visibleRect(hint.selector);
      if (rect) {
        setAnchor((prev) =>
          prev &&
          prev.hint.id === hint.id &&
          prev.rect.top === rect.top &&
          prev.rect.left === rect.left
            ? prev
            : { hint, rect }
        );
        return;
      }
    }
    setAnchor(null);
  }, [location.pathname, userKey]);

  useEffect(() => {
    scan();
    const timer = window.setInterval(scan, SCAN_INTERVAL_MS);
    window.addEventListener('resize', scan);
    window.addEventListener('scroll', scan, true);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', scan);
      window.removeEventListener('scroll', scan, true);
    };
  }, [scan]);

  if (!anchor || !userKey) return null;
  const { hint, rect } = anchor;

  const close = (action: 'dismiss' | 'snooze' | 'all') => {
    if (action === 'dismiss') dismissHint(userKey, hint.id);
    if (action === 'snooze') snoozeHint(hint.id);
    if (action === 'all') disableAllHints(userKey);
    mixpanelService.track('Feature Hint Closed', { hint: hint.id, action });
    setAnchor(null);
    setVersion((v) => v + 1);
  };

  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed z-[70] rounded-lg ring-2 ring-cyan-400/80 animate-pulse"
        style={{
          left: rect.left - 4,
          top: rect.top - 4,
          width: rect.width + 8,
          height: rect.height + 8,
        }}
      />
      <section
        role="dialog"
        aria-label={hint.title}
        data-testid="feature-hint"
        className="fixed z-[71] rounded-lg border border-cyan-400/40 bg-v2-surface p-3.5 shadow-2xl animate-v2-fade-in"
        style={position(rect, hint.placement)}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-v2-text-primary">
            <Sparkles className="h-4 w-4 shrink-0 text-cyan-400" aria-hidden="true" />
            {hint.title}
          </h2>
          <button
            type="button"
            onClick={() => close('snooze')}
            className="rounded p-0.5 text-v2-text-tertiary hover:bg-v2-hover-bg hover:text-v2-text-primary"
            aria-label="Close tip"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1.5 text-xs leading-5 text-v2-text-secondary">{hint.body}</p>
        <div className="mt-3 flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => close('snooze')}
            className="whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-v2-text-secondary hover:bg-v2-hover-bg"
          >
            Remind me later
          </button>
          <button
            type="button"
            onClick={() => close('dismiss')}
            className="whitespace-nowrap rounded-md bg-v2-accent px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90"
          >
            Don&apos;t show again
          </button>
        </div>
        <button
          type="button"
          onClick={() => close('all')}
          className="mt-2 text-[11px] text-v2-text-muted hover:text-v2-text-secondary hover:underline"
        >
          Hide all tips like this
        </button>
      </section>
    </>
  );
};

export default FeatureHints;
